import React from "react";
import "./ClearMemoryCard.css";

/*
 * "Clear BossDrop's memory" card, at the bottom of the Memory page (moved from
 * the Control Room on Nov 1 2026; it replaced "Clear fingerprints").
 *
 * POST /api/memory/clear with { scopes: [...] }. The backend owns the meaning
 * of each scope (server/utils/memoryAdmin.js, MEMORY_RESET_SCOPES) and derives
 * the workspace from the session; this card only says which scopes to clear
 * and reports what the server says it removed. Admin-only: the route is
 * requireWorkspaceAdmin, and every control here is disabled without
 * canManageWorkspace. `onCleared` runs after anything was cleared.
 */

export const CLEAR_MEMORY_TITLE = "Clear BossDrop's memory";

export const CLEAR_MEMORY_OPTIONS = [
  {
    id: "learned",
    label: "What BossDrop has learned",
    help: "Everything above: what it measured about how your team runs, your corrections, what your team told it, and the history. It starts learning again from today, so export first if you want a copy.",
    defaultChecked: true,
  },
  {
    id: "outcomes",
    label: "What worked before",
    help: "Its record of which nudges and escalations got results.",
    defaultChecked: true,
  },
  {
    id: "repeat_history",
    label: "Repeat-prevention history",
    help: "Lets BossDrop redo things it already did, so it may re-send a ping, report or meeting it sent recently.",
    defaultChecked: false,
  },
  {
    id: "email_history",
    label: "Email conversation history with ai@",
    help: "Past email threads; replies to old threads start fresh.",
    defaultChecked: false,
  },
];

const LABEL_BY_ID = Object.fromEntries(CLEAR_MEMORY_OPTIONS.map((o) => [o.id, o.label]));

function defaultSelection() {
  return Object.fromEntries(CLEAR_MEMORY_OPTIONS.map((o) => [o.id, o.defaultChecked]));
}

function count(v) {
  return typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : null;
}

function plural(n, one, many) {
  return `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;
}

function lowerFirst(s) {
  return s ? s.charAt(0).toLowerCase() + s.slice(1) : s;
}

/** The counted parts of one scope's result, in words. */
function scopeParts(scope, tables) {
  const t = tables && typeof tables === "object" ? tables : {};
  const parts = [];
  if (scope === "learned") {
    const facts = count(t.workspace_memory);
    const summaries = count(t.memory_rollups);
    const snippets = count(t.memory_embeddings);
    const moments = count(t.work_observations);
    if (facts !== null) parts.push(plural(facts, "learned fact", "learned facts"));
    if (moments) parts.push(plural(moments, "observed moment", "observed moments"));
    if (summaries) parts.push(plural(summaries, "weekly summary", "weekly summaries"));
    if (snippets) parts.push(plural(snippets, "indexed snippet", "indexed snippets"));
  } else if (scope === "outcomes") {
    const n = count(t.action_outcomes);
    if (n !== null) parts.push(plural(n, "outcome", "outcomes"));
  } else if (scope === "repeat_history") {
    const n = count(t.automation_fingerprints);
    if (n !== null) parts.push(plural(n, "repeat-prevention record", "repeat-prevention records"));
  } else if (scope === "email_history") {
    const n = count(t.ai_email_messages);
    if (n !== null) parts.push(plural(n, "email message", "email messages"));
  }
  return parts;
}

/**
 * Turn the server's answer into the card's two lines:
 *   cleared  "Cleared 42 learned facts, 13 outcomes."  (scopes fully done)
 *   failed   "Couldn't clear what worked before. Try again."
 * A scope that only partly went is reported as failed, with nothing claimed.
 */
export function describeClearResult(json, requested = []) {
  const results = json && typeof json.results === "object" && json.results ? json.results : {};
  const order = CLEAR_MEMORY_OPTIONS.map((o) => o.id);
  const cleared = [];
  const failed = [];
  for (const scope of order) {
    if (!requested.includes(scope) && !results[scope]) continue;
    const r = results[scope];
    if (r && r.ok === true) cleared.push(...scopeParts(scope, r.tables));
    else failed.push(lowerFirst(LABEL_BY_ID[scope]));
  }
  return {
    cleared: cleared.length ? `Cleared ${cleared.join(", ")}.` : "",
    failed: failed.length ? `Couldn't clear ${failed.join("; ")}. Try again.` : "",
  };
}

/** Words for a request that failed as a whole. */
export function describeClearError(status) {
  if (status === 0) return "Couldn't reach BossDrop. Check your connection and try again.";
  if (status === 401) return "Your session has ended. Sign in again, then try again.";
  if (status === 403) return "Only workspace admins can clear BossDrop's memory.";
  if (status === 400) return "BossDrop didn't recognize that selection. Reload the page and try again.";
  if (status === 429) return "Too many requests. Wait a moment and try again.";
  return "BossDrop couldn't clear its memory right now. Try again in a minute.";
}

export default function ClearMemoryCard({ apiBase, canManageWorkspace, onCleared = null }) {
  const [selected, setSelected] = React.useState(defaultSelection);
  // idle -> confirm -> busy -> idle
  const [phase, setPhase] = React.useState("idle");
  const [outcome, setOutcome] = React.useState(null); // { cleared, failed }
  const aliveRef = React.useRef(true);
  React.useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const picked = CLEAR_MEMORY_OPTIONS.filter((o) => selected[o.id]).map((o) => o.id);
  const canAct = !!canManageWorkspace;
  const locked = !canAct || phase !== "idle";

  const toggle = (id, checked) => {
    setSelected((prev) => ({ ...prev, [id]: checked }));
    setOutcome(null);
  };

  const askConfirm = () => {
    if (!canAct || !picked.length) return;
    setOutcome(null);
    setPhase("confirm");
  };

  const runClear = async () => {
    if (!canAct || !picked.length) return;
    const scopes = picked;
    setPhase("busy");
    let status = 0;
    let json = null;
    try {
      const base = String(apiBase || "").replace(/\/+$/, "");
      const response = await fetch(`${base}/api/memory/clear`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ scopes }),
      });
      status = response.status;
      json = await response.json().catch(() => null);
      if (!aliveRef.current) return;
      const hasResults = json && json.results && typeof json.results === "object";
      if (!response.ok && !hasResults) {
        setOutcome({ cleared: "", failed: describeClearError(status) });
      } else {
        const described = describeClearResult(json, scopes);
        setOutcome(
          described.cleared || described.failed
            ? described
            : { cleared: "", failed: describeClearError(status) },
        );
        if (described.cleared) onCleared?.();
      }
    } catch (error) {
      console.warn("clear memory failed", error?.message || error);
      if (!aliveRef.current) return;
      setOutcome({ cleared: "", failed: describeClearError(0) });
    } finally {
      if (aliveRef.current) setPhase("idle");
    }
  };

  return (
    <div className="panelSub" style={{ marginTop: 12 }}>
      <h3 className="crSubTitle">{CLEAR_MEMORY_TITLE}</h3>
      <p className="muted" style={{ marginTop: 4 }}>
        {"BossDrop learns from your workspace as it works, and this resets the parts you pick below."}
      </p>

      <div className="clearMemory__options">
        {CLEAR_MEMORY_OPTIONS.map((o) => (
          <label
            key={o.id}
            className={[
              "clearMemory__option",
              selected[o.id] ? "is-checked" : "",
              locked ? "is-locked" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <input
              className="bd-check"
              type="checkbox"
              checked={!!selected[o.id]}
              disabled={locked}
              onChange={(e) => toggle(o.id, e.target.checked)}
            />
            <span className="clearMemory__text">
              <span className="clearMemory__label">{o.label}</span>
              <span className="clearMemory__help muted small">{o.help}</span>
            </span>
          </label>
        ))}
      </div>

      {phase === "idle" ? (
        <div className="clearMemory__actions">
          <button
            type="button"
            className="btn btnDangerGhost"
            onClick={askConfirm}
            disabled={!canAct || !picked.length}
          >
            Clear selected
          </button>
          {!picked.length && canAct ? (
            <span className="muted small">{"Pick at least one thing to clear."}</span>
          ) : null}
        </div>
      ) : (
        <div
          role="group"
          aria-label="Confirm clearing BossDrop's memory"
          className="clearMemory__confirm"
        >
          <div className="clearMemory__confirmTitle">{"This will permanently clear:"}</div>
          <ul className="clearMemory__confirmList">
            {picked.map((id) => (
              <li key={id}>{LABEL_BY_ID[id]}</li>
            ))}
          </ul>
          <div className="muted small" style={{ marginTop: 6 }}>
            {"This can't be undone."}
          </div>
          <div className="clearMemory__actions" style={{ marginTop: 10 }}>
            <button
              type="button"
              className="btn btnDangerGhost"
              onClick={runClear}
              disabled={phase === "busy"}
            >
              {phase === "busy" ? "Clearing..." : "Yes, clear"}
            </button>
            <button
              type="button"
              className="btn ghost"
              onClick={() => setPhase("idle")}
              disabled={phase === "busy"}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {outcome?.cleared ? (
        <div role="status" className="clearMemory__result small">
          {outcome.cleared}
        </div>
      ) : null}
      {outcome?.failed ? (
        <div role="alert" className="clearMemory__error small">
          {outcome.failed}
        </div>
      ) : null}
    </div>
  );
}
